import * as React from 'react'

import { Box, Form, Icon, Text } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import { CardholderInformationQuery } from 'generated/resources/graphql'

import DayPicker from 'modules/core/components/DayPicker'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import MoneyField from 'modules/core/components/MoneyField'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { RequestCardForm } from 'modules/expenses/pages/my_expenses/forms'

import { DialCodeSelector } from '../../common/DialCodeSelector'

interface ContentProps {
  currentInformation: CardholderInformationQuery['employees']['employee'] | undefined
  form: FormikProps<RequestCardForm>
}

const Content = ({ form, currentInformation }: ContentProps) => {
  const locale = useCurrentSession().access.locale
  const cardholder = currentInformation?.bankingCardholder
  const currency = cardholder?.currency || currentInformation?.company?.currency || 'EUR'
  const cardholderExists = !!cardholder

  return (
    <Form>
      <Box gap="s32">
        {!cardholderExists && (
          <Box gap="s16">
            <SectionHeader
              title={i18n.t('expenses_v2.forms.fields.header_request_card_cardholder.title')}
              description={i18n.t(
                'expenses_v2.forms.fields.header_request_card_cardholder.description'
              )}
              icon="user-edit"
            />
            <Box flexDirection="row" gap="s16">
              <TextField
                mandatory
                label={i18n.t('expenses_v2.forms.fields.first_name.label')}
                secondaryLabel={
                  <InformationTooltip
                    information={i18n.t('expenses_v2.forms.fields.first_name.info')}
                    icon="question"
                  />
                }
                value={form.values.firstName}
                error={form.touched.firstName ? form.errors.firstName : ''}
                onChange={(value: string) => form.setFieldValue('firstName', value)}
                onBlur={() => form.setFieldTouched('firstName', true)}
              />
              <TextField
                label={i18n.t('expenses_v2.forms.fields.last_name.label')}
                mandatory
                value={form.values.lastName}
                error={form.touched.lastName ? form.errors.lastName : ''}
                onChange={(value: string) => form.setFieldValue('lastName', value)}
                onBlur={() => form.setFieldTouched('lastName', true)}
              />
            </Box>
            <DayPicker
              label={i18n.t('software_management.initial_setup.wizard.cardholder_form.birth_date')}
              onChange={(value) => form.setFieldValue('dateOfBirth', dateUIToBackend(value), true)}
              value={form.values.dateOfBirth ? dateBackendToUI(form.values.dateOfBirth) : ''}
              mandatory
              error={form.touched.dateOfBirth ? form.errors.dateOfBirth : ''}
              onToggle={(open) => {
                if (!open) {
                  form.setFieldTouched('dateOfBirth', true)
                }
              }}
            />
            <Box flexDirection="row" gap="s16">
              <Box gap="s6">
                <DialCodeSelector
                  label={i18n.t(
                    'software_management.initial_setup.wizard.cardholder_form.country_code'
                  )}
                  onChange={(value) => form.setFieldValue('phoneCountryCode', value, true)}
                  onClose={() => form.setFieldTouched('phoneCountryCode', true, false)}
                  value={form.values.phoneCountryCode}
                  error={form.touched.phoneCountryCode ? form.errors.phoneCountryCode : ''}
                  locale={locale}
                  mandatory
                />
                <Text color="grey700" size="200">
                  {i18n.t('expenses_v2.forms.fields.sending_options.country_phone_number_example')}
                </Text>
              </Box>
              <Box gap="s6" flexGrow>
                <TextField
                  label={i18n.t(
                    'software_management.initial_setup.wizard.cardholder_form.phone_number'
                  )}
                  secondaryLabel={
                    <InformationTooltip
                      information={i18n.t('expenses_v2.forms.fields.mobile_phone_number.info')}
                      icon="question"
                    />
                  }
                  onChange={(value) => form.setFieldValue('phoneNumber', value, false)}
                  value={form.values.phoneNumber}
                  mandatory
                  error={form.touched.phoneNumber ? form.errors.phoneNumber : ''}
                  onBlur={() => form.setFieldTouched('phoneNumber', true)}
                />
                <Text color="grey700" size="200">
                  {i18n.t('expenses_v2.forms.fields.sending_options.phone_number_example')}
                </Text>
              </Box>
            </Box>
          </Box>
        )}
        <Box gap="s16">
          <SectionHeader
            title={i18n.t('expenses_v2.forms.fields.header_request_card.title')}
            description={i18n.t('expenses_v2.forms.fields.header_request_card.description')}
            icon="credit-card"
          />
          <Box gap="s6">
            <MoneyField
              mandatory
              placeholder="0"
              currency={currency}
              error={form.touched.spendingAmount ? form.errors.spendingAmount : ''}
              label={i18n.t('software_management.initial_setup.wizard.costs_form.budget')}
              value={form.values.spendingAmount}
              onChange={(value) => form.setFieldValue('spendingAmount', value)}
              onBlur={() => form.setFieldTouched('spendingAmount', true)}
            />
          </Box>
          <Box gap="s6">
            <TextField
              label={i18n.t('expenses_v2.forms.fields.card_alias_non_optional.label')}
              value={form.values.alias}
              error={form.touched.alias ? form.errors.alias : ''}
              onChange={(value) => form.setFieldValue('alias', value)}
              onBlur={() => form.setFieldTouched('alias', true)}
              placeholder={i18n.t('expenses_v2.forms.fields.card_alias_non_optional.placeholder')}
            />
            <Text color="grey700" size="200">
              {i18n.t('expenses_v2.forms.fields.card_alias_non_optional.example')}
            </Text>
          </Box>
          <Box gap="s6">
            <TextArea
              mandatory
              label={i18n.t('expenses_v2.forms.fields.card_request_description.label')}
              value={form.values.description}
              error={form.touched.description ? form.errors.description : ''}
              onChange={(value) => form.setFieldValue('description', value)}
              onBlur={() => form.setFieldTouched('description', true)}
              placeholder={i18n.t('expenses_v2.forms.fields.card_request_description.placeholder')}
            />
          </Box>
        </Box>
      </Box>
    </Form>
  )
}

interface SectionHeaderProps {
  title: string
  description: string
  icon: React.ComponentProps<typeof Icon.Large>['icon']
}

const SectionHeader = ({ title, description, icon }: SectionHeaderProps) => (
  <Box gap="s8">
    <Icon.Large icon={icon} color="radicalRed1200" />
    <Text color="grey1000" size="400" weight="medium">
      {title}
    </Text>
    <Text color="grey1000" size="300" weight="normal">
      {description}
    </Text>
  </Box>
)

export default Content
